import React, { useState, useEffect } from 'react';
import { Container, Grid, TextField, Checkbox, InputAdornment } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { DealStage, DealType, DealSource, DealReasonForLoss } from '../../../utils/enums'
import UsersAutoComplete from '../../Users/UsersAutoComplete';
import dayjs from 'dayjs';
import { DatePicker, DateTimePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import AccountsAutoComplete from '../../Accounts/AccountsAutoComplete';
import ContactsAutoComplete from '../../Contacts/ContactsAutoComplete';
import CampaignsAutoComplete from '../../Campaigns/CampaignsAutoComplete';
import { CommentSharp } from '@mui/icons-material';


const EditForm = ({ dealData, onSubmit, loading }) => {
    const [ownerId, setOwnerId] = useState('');
    const [dealName, setDealName] = useState('');
    const [country, setCountry] = useState('');
    const [accountId, setAccountId] = useState(0);
    const [amount, setAmount] = useState(null);
    const [closingDate, setClosingDate] = useState(dayjs());
    const [stage, setStage] = useState(0);
    const [probability, setProbability] = useState(0);
    const [type, setType] = useState(0);
    const [reasonForLoss, setReasonForLoss] = useState(0);
    const [expectedRevenue, setExpectedRevenue] = useState(0);
    const [leadSource, setLeadSource] = useState(0);
    const [contactId, setContactId] = useState(null);
    const [nextStep, setNextStep] = useState('');
    const [campaignId, setCampaignId] = useState(null);
    const [campaignSource, setCampaignSource] = useState('');
    const [description, setDescription] = useState('');
    const [shouldFetchSingleContact, setShouldFetchSingleContact] = useState(false);
    const [shouldFetchSingleAccount, setShouldFetchSingleAccount] = useState(false);

    useEffect(() => {
        if (dealData?.id > 0) {
            setOwnerId(dealData.ownerId || '');
            setDealName(dealData.dealName || '');
            setCountry(dealData.country || '');
            setAccountId(dealData.accountId || 0);
            setShouldFetchSingleAccount(true);
            setAmount(dealData.amount || null);
            setClosingDate(dealData.closingDate ? dayjs(dealData.closingDate) : dayjs());
            setStage(dealData.stage || 0);
            setProbability(dealData.probability || 0);
            setType(dealData.type || 0);
            setReasonForLoss(dealData.reasonForLoss || 0)
            setExpectedRevenue(dealData.expectedRevenue || 0);
            setLeadSource(dealData.leadSource || 0);
            setContactId(dealData.contactId || null);
            setShouldFetchSingleContact(true);
            setNextStep(dealData.nextStep || '');
            setCampaignId(dealData.campaignId || null);
            setCampaignSource(dealData.campaignSource || '');
            setDescription(dealData.description || '');
        }
    }, [dealData]);

    const handleSubmit = (e) => {
        e.preventDefault();
        // Collect all form data
        const formData = {
            ownerId,
            dealName,
            country,
            accountId,
            amount,
            closingDate,
            stage,
            probability,
            type,
            expectedRevenue,
            leadSource,
            contactId,
            nextStep,
            campaignId,
            campaignSource,
            description,
            reasonForLoss
        };
        // Call onSubmit function passed as a prop
        onSubmit(formData);
    };

    const handleStageChange = (newValue) => {

        setStage(newValue ? newValue.value : '');
        const selectedStage = DealStage.find((option) => option.value === newValue?.value);

        if (selectedStage) {
            setProbability(selectedStage.probability); // Update probability as percentage
            calculateExpectedRevenue(amount, selectedStage.probability);
        }
    };

    const handleAmountChange = (e) => {
        const newAmount = e.target.value;
        setAmount(newAmount);
        const selectedStage = DealStage.find((option) => option.value === stage);

        if (selectedStage) {
            calculateExpectedRevenue(newAmount, selectedStage.probability);
        }
    };

    const calculateExpectedRevenue = (amount, probability) => {
        if (!amount || isNaN(amount)) return setExpectedRevenue(0);

        setExpectedRevenue(amount * (probability / 100));
    };


    return (
        <form id='create-form' onSubmit={handleSubmit}>
            <Container maxWidth="md" sx={{ marginTop: "10px" }}>
                <h4>Deal Information</h4>
                <Grid container spacing={3}>
                    {/* Deal Owner */}
                    <Grid item xs={12} sm={6}>
                        <UsersAutoComplete setUserId={setOwnerId} userId={ownerId} />
                    </Grid>

                    {/* Amount ****** */}
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Amount"
                            value={amount}
                            onChange={handleAmountChange}
                            InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            }} />
                    </Grid>

                    {/* Deal Name */}
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Deal Name" required
                            value={dealName}
                            onChange={(e) => setDealName(e.target.value)} />
                    </Grid>

                    {/* Country */}
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Country"
                            value={country}
                            onChange={(e) => setCountry(e.target.value)} />
                    </Grid>

                    {/* Closing Date */}
                    <Grid item xs={12} sm={6}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                label="Closing Date"
                                fullWidth
                                required
                                value={closingDate}
                                onChange={(newDate) => setClosingDate(newDate)}
                                slotProps={{
                                    textField: {
                                        required: 'true',
                                        variant: 'outlined',
                                        fullWidth: true
                                    },
                                }}
                            />
                        </LocalizationProvider>
                    </Grid>

                    {/* Account Name */}
                    <Grid item xs={12} sm={6}>
                        <AccountsAutoComplete required={true} accountId={accountId} setAccountId={setAccountId} shouldFetchSingleAccount={shouldFetchSingleAccount} />
                    </Grid>

                    {/* Stage */}
                    <Grid item xs={12} sm={6}>
                        <Autocomplete
                            disablePortal
                            required
                            options={DealStage}
                            sx={{ width: "100%" }}
                            value={DealStage.find((option) => option.value === stage)}
                            onChange={(event, newValue) => handleStageChange(newValue)}
                            renderInput={(params) => <TextField {...params} label="Stage" />}
                        />
                    </Grid>

                    {(stage === 7 || stage === 8) &&
                        <Grid item xs={12} sm={6}>
                            <Autocomplete
                                disablePortal
                                required
                                options={DealReasonForLoss}
                                sx={{ width: "100%" }}
                                value={DealReasonForLoss.find((option) => option.value === reasonForLoss)}
                                onChange={(event, newValue) => setReasonForLoss(newValue ? newValue.value : '')}
                                renderInput={(params) => <TextField {...params} label="Reason For Loss" />}
                            />
                        </Grid>
                    }

                    {/* Type */}
                    <Grid item xs={12} sm={6}>
                        <Autocomplete
                            disablePortal
                            options={DealType}
                            sx={{ width: "100%" }}
                            value={DealType.find((option) => option.value === type)}
                            onChange={(event, newValue) => setType(newValue ? newValue.value : '')}
                            renderInput={(params) => <TextField {...params} label="Type" />}
                        />
                    </Grid>

                    {/* Probability */}
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Probability (%)"
                            value={probability}
                            onChange={(e) => setProbability(e.target.value)} />
                    </Grid>

                    {/* Expected Revenue */}
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Expected Revenue"
                            disabled
                            value={expectedRevenue}
                            onChange={(e) => setExpectedRevenue(e.target.value)} />
                    </Grid>

                    {/* Next Step */}
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Next Step"
                            value={nextStep}
                            onChange={(e) => setNextStep(e.target.value)} />
                    </Grid>

                    {/* Deal Source */}
                    <Grid item xs={12} sm={6}>
                        <Autocomplete
                            disablePortal
                            options={DealSource}
                            sx={{ width: "100%" }}
                            value={DealSource.find((option) => option.value === leadSource)}
                            onChange={(event, newValue) => setLeadSource(newValue ? newValue.value : '')}
                            renderInput={(params) => <TextField {...params} label="Lead Source" />}
                        />
                    </Grid>

                    {/* Campaign Source */}
                    <Grid item xs={12} sm={6}>
                        <CampaignsAutoComplete campaignId={campaignId} setCampaignId={setCampaignId} shouldFetchSingleCampaign={true} />
                    </Grid>

                    {/* Contact Name */}
                    <Grid item xs={12} sm={6}>
                        <ContactsAutoComplete contactId={contactId} setContactId={setContactId} shouldFetchSingleContact={shouldFetchSingleContact} />
                    </Grid>

                </Grid>
                <br />
                <h4>Description Information</h4>
                <Grid container spacing={3}>
                    {/* Description */}
                    <Grid item xs={12} sm={12}>
                        <TextField multiline fullWidth label="Description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)} />
                    </Grid>
                </Grid >
            </Container >
        </form>
    );
};

export default EditForm;
